import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useLockListState } from "@api/lock.api";
import { getLockDate, isLockedByDateOnly } from "@helpers/lock";

const EMPTY_LOCK_DATES = new Map<number, Date | null>();

interface PeriodLockDatesContextValue {
    lockDateByEntityId: ReadonlyMap<number, Date | null>;
    isResolved: boolean;
}

const PeriodLockDatesContext = createContext<PeriodLockDatesContextValue>({
    lockDateByEntityId: EMPTY_LOCK_DATES,
    isResolved: false,
});

interface PeriodLockDatesProviderProps {
    entityIds: number[];
    children: ReactNode;
}

export function PeriodLockDatesProvider({
    entityIds,
    children,
}: PeriodLockDatesProviderProps) {
    const uniqueEntityIds = useMemo(
        () => [...new Set(entityIds)].sort((left, right) => left - right),
        [entityIds],
    );
    const { locks, isResolved } = useLockListState(uniqueEntityIds);
    const value = useMemo<PeriodLockDatesContextValue>(() => {
        const lockDateByEntityId = new Map<number, Date | null>();
        for (const lock of locks) {
            lockDateByEntityId.set(lock.entityId, getLockDate([lock]));
        }
        return { lockDateByEntityId, isResolved };
    }, [locks, isResolved]);

    return (
        <PeriodLockDatesContext.Provider value={value}>
            {children}
        </PeriodLockDatesContext.Provider>
    );
}

export function useIsEntityDateLocked(
    entityId: number | null,
    dateOnly?: string | null,
) {
    const { lockDateByEntityId } = useContext(PeriodLockDatesContext);
    const lockDate =
        entityId == null ? null : (lockDateByEntityId.get(entityId) ?? null);

    return isLockedByDateOnly(lockDate, dateOnly);
}

/**
 * Whether the period-lock query has resolved. Before it resolves the lock
 * dates are unknown, so locked-period gating should fail safe (treat editable
 * fields as locked) rather than briefly permitting edits.
 */
export function useIsPeriodLockResolved(): boolean {
    return useContext(PeriodLockDatesContext).isResolved;
}
